import { FormattedMessage } from 'react-intl';
import { Media, UploadState, UploadType } from '@synergy/models';
import { PaloConfirmationDialog } from '@synergy/components-palo';
import { PoolFormattedMessage } from '@synergy/components';
import { ReactElement, useState } from 'react';

import { DocumentRecord } from '../../../features/contacts-and-organizations/components/documents/documents-table';
import { PaloAttachments } from './palo-attachments';

export interface PaloRecordDocumentsProps<T extends DocumentRecord> {
    documents: T[];
    downloadBaseUrl: string;
    onAdd: (files: File[], titles: string[]) => void;
    onRename: (document: T, title: string) => void;
    onRemove: (document: T) => void;
}

/** A master-data document in the shape the file list reads. */
const asMedia = (document: DocumentRecord): Media =>
    Media.fromPlainObject({
        id: document.id,
        title: document.title,
        originalName: document.originalName ?? document.name ?? document.title ?? '',
        pathname: document.pathname ?? '',
        mimeType: document.mimeType ?? '',
        size: document.size ?? 0,
        createdAt: document.createdAt,
        type: UploadType.Internal,
        progress: document.uploadProgress,
        state: undefined === document.uploadProgress ? UploadState.Finished : UploadState.InProgress,
    });

/**
 * The documents of a supplier, a member or a contact, in the rail beside the
 * mask — the same place and the same list as the files of a document.
 *
 * They lived behind a tab of their own, the only files in the tool that did.
 * The rail was asked for "komplett durchgezogen" (meeting 2026-09-22): wherever
 * there are files, they are on the right.
 */
export const PaloRecordDocuments = <T extends DocumentRecord>({
    documents,
    downloadBaseUrl,
    onAdd,
    onRename,
    onRemove,
}: PaloRecordDocumentsProps<T>): ReactElement => {
    const byId = (media: Media): T | undefined => documents.find(document => document.id === media.id);

    /**
     * Asked before it goes, as it was in the documents tab: a contract is
     * removed for good, and the cross sits right beside the pencil.
     */
    const [toRemove, setToRemove] = useState<T | undefined>(undefined);

    /**
     * Newest first, as in the documents table (#1469): a supplier can carry
     * forty papers, and the one just added belongs at the top, not the foot.
     * A file still on its way up has no date yet and leads.
     */
    const newestFirst = [...documents].sort(
        (a, b) =>
            (b.createdAt ? new Date(b.createdAt).getTime() : Number.MAX_SAFE_INTEGER) -
            (a.createdAt ? new Date(a.createdAt).getTime() : Number.MAX_SAFE_INTEGER)
    );

    return (
        <>
            <PaloAttachments
                groups={[
                    {
                        key: 'documents',
                        files: newestFirst.map(asMedia),
                        downloadBaseUrl,
                        onDrop: (files, titles) => onAdd(files, titles ?? []),
                        onRename: (media, title) => {
                            const document = byId(media);

                            if (document) {
                                onRename(document, title);
                            }
                        },
                        onRemove: media => setToRemove(byId(media)),
                    },
                ]}
            />

            <PaloConfirmationDialog
                isVisible={undefined !== toRemove}
                title={<FormattedMessage id="app.contactsAndOrganizations.documents.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                onCancel={() => setToRemove(undefined)}
                onConfirm={() => {
                    if (toRemove) {
                        onRemove(toRemove);
                    }
                    setToRemove(undefined);
                }}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.documents.deleteWarning"
                    values={{ name: toRemove?.title || toRemove?.originalName }}
                />
            </PaloConfirmationDialog>
        </>
    );
};
